/**
 * `schedule` — crew logistics: who does what, when. PII-scoped per person (P3).
 * Deliberately distinct from `timetable` (the event's public running order),
 * which ships with the full registry in P3 — see ARCHITECTURE.md §8.5 / ADR-007.
 *
 * All domain times are venue-local wall time (ARCHITECTURE.md §12): rendered
 * exactly as entered, never tz-converted on display.
 */
import { z } from "zod";
import { defineCollection, field } from "../define.ts";

/** YYYY-MM-DD, venue-local (§12). */
const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD (venue-local)");
/** HH:MM 24h, venue-local wall time (§12). */
const WALL_TIME = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM venue-local wall time");

export const schedule = defineCollection({
  key: "schedule", // the ONLY place this string is born (CONSTRAINTS #5)
  labelKey: "collections.schedule",
  icon: "calendar-clock",
  sheetTab: "Schedule", // identity is DeveloperMetadata td_collection, not the tab name
  dedupFields: ["day", "start_time", "title"], // -> op dedup_key (§4.1)
  sort: [{ field: "day" }, { field: "start_time" }],
  split: "day", // NOTE: canonical 4-split names are pinned by the P3 UI work
  chatHint:
    "Crew logistics: who does what, when — lobby calls, soundcheck, doors, the set, load-out, " +
    "meals, meet points. NOT the event's public running order: other artists' set times belong " +
    "in the timetable collection (ships in P3), never here.",
  fields: {
    title: field({
      type: z.string().min(1),
      tier: "hot",
      pii: "crew",
      labelKey: "schedule.title",
      renamedFrom: ["label"], // v1 wire name (migration lineage, CONSTRAINTS #6)
      sheet: { column: "Title" },
      suggestedChips: ["Lobby call", "Soundcheck", "Doors", "Set", "Load-out"],
      description: "What happens — e.g. lobby call, soundcheck, doors, DJ set, load-out, dinner.",
    }),
    day: field({
      type: DAY.optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "schedule.day",
      renamedFrom: ["date"], // v1 wire name (migration lineage)
      sheet: { column: "Day" },
      description: "Date of the entry (YYYY-MM-DD, venue-local). Omit for single-day gigs: defaults to the gig date.",
    }),
    end_day: field({
      type: DAY.optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "schedule.end_day",
      renamedFrom: ["end_date"], // v1 wire name (multi-day entries)
      sheet: { column: "End day" },
      description: "Last day for multi-day entries (YYYY-MM-DD, venue-local). Empty for single-day.",
    }),
    start_time: field({
      type: WALL_TIME.optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "schedule.start_time",
      renamedFrom: ["time"], // v1 wire name (migration lineage, CONSTRAINTS #6)
      sheet: { column: "Start" },
      description: "Start time (HH:MM), venue-local wall time — rendered exactly as entered, never tz-converted.",
    }),
    end_time: field({
      type: WALL_TIME.optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "schedule.end_time",
      sheet: { column: "End" },
      description: "End time (HH:MM), venue-local wall time. Optional — most entries only need a start.",
    }),
    place: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "crew",
      labelKey: "schedule.place",
      renamedFrom: ["location"], // v1 wire name (migration lineage, CONSTRAINTS #6)
      sheet: { column: "Place" },
      description: "Where it happens — venue area, hotel lobby, restaurant, meeting point.",
    }),
    person_id: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "schedule.person",
      sheet: { column: "Person" },
      description:
        "Optional: the crew member this entry is for. Empty = the whole party. " +
        "Per-person row scoping arrives with crew keys and the crew collection in P3.",
    }),
    notes: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "crew",
      labelKey: "schedule.notes",
      sheet: { column: "Notes" },
      description: "Free-form notes for this entry (access details, contact on site, reminders).",
    }),
  },
});
